// @flow
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import classNames from 'classnames/bind';
import { DEFAULT_COUNTRIES, getLocalizedCountry } from '../../../../local-values/utils/countries';
import { FormValidator } from '../../../../../utils/form';
import Select from '../../../../../elements/Select/Select';
import Cleave from 'cleave.js/react';
import CleavePhone from 'cleave.js/dist/addons/cleave-phone.i18n';

import style from './PhoneForm.scss';
import formStyle from '../../../../../assets/styles/form.scss';
import inputStyle from '../../../../../assets/styles/input.scss';
import buttonStyle from '../../../../../assets/styles/button.scss';

import type { Option } from '../../../../../elements/Select/Select';
import type { LocalValuesState } from '../../../../local-values/duck';
import type { LocalValueCountry } from '../../../../local-values/utils/countries';
import Checkbox from "../../../../../elements/Checkbox/Checkbox";

type Props = {
  handleChangeCountry: (option: Option) => void;
  handleChange: (e: Event) => void;
  handleFocus: (e: Event) => void;
  handleSubmitPhone: (e: Event, resend?: boolean) => void;
  handleWillVerify?: (e: Event) => void;
  handleAcceptReceiveSMS?: (e: Event) => void;
  isPostingPhone?: boolean;
  country: {
    value: string;
    errors?: string[];
  };
  phone: {
    value: string;
    pristine: boolean;
    errors?: string[];
  };
  color: 'orange' | 'light-blue';
  localValues: LocalValuesState;
  agreeToReceiveSMS?: boolean;
  showAcceptSMS?: boolean;
  isPhoneVerified?: boolean;
};

const messages = defineMessages({
  country: {
    placeholder: {
      id: 'preferences.security.phone.countryPhone.placeholder'
    }
  },
  phone: {
    placeholder: {
      id: 'preferences.security.phone.phone.placeholder',
    }
  },
});

export default class PhoneForm extends Component<Props> {
  static defaultProps = {
    showAcceptSMS: true,
  };

  render() {
    const {
      handleSubmitPhone, handleChangeCountry, handleChange, handleFocus, isPostingPhone, country, phone, color,
      localValues, agreeToReceiveSMS, handleAcceptReceiveSMS, handleWillVerify, showAcceptSMS, isPhoneVerified,
    } = this.props;
    const countryOptions: Option[] = (localValues.countries || DEFAULT_COUNTRIES).map((country: LocalValueCountry) => ({
      value: country.key,
      label: `${getLocalizedCountry(country.key, this.context.intl.formatMessage, localValues.countriesMap)} (${country.phonePrefix})`,
      image: country.icon,
    }));
    const countryError = country.errors && FormValidator.buildErrorLabel(country.errors, {
      required: 'preferences.security.phone.countryPhone.error.required',
    });
    const phoneError = phone.errors && FormValidator.buildErrorLabel(phone.errors, {
      required: 'preferences.security.phone.phone.error.required',
    });
    const countryPhonePrefix = (country.value && localValues.countriesMap) && localValues.countriesMap[country.value].phonePrefix;
    return (
      <form className={classNames(formStyle.formWrap, style.inputs)}>
        <section className={classNames(formStyle.row, formStyle.inputRow)}>
          <FormattedMessage id="preferences.security.phone.countryPhone.label">{
            txt => <label className={formStyle.label}>{txt}</label>
          }</FormattedMessage>
          <Select value={country.value}
                  handleChange={handleChangeCountry}
                  searchable={true}
                  placeholder={this.context.intl.formatMessage(messages.country.placeholder)}
                  options={countryOptions}
                  disabled={isPostingPhone}/>
          {countryError}
        </section>
        <section className={classNames(formStyle.row, formStyle.inputRow)}>
          <FormattedMessage id="preferences.security.phone.phone.label">{
            txt => (<label className={formStyle.label}>{txt}</label>)
          }</FormattedMessage>
          <label className={classNames(style.prefix, { [style.error]: phone.errors && phone.errors.length })}>
            {country.value ? countryPhonePrefix : ''}
          </label>
          <Cleave value={phone.value || ''}
                  options={{
                    phone: true,
                    phoneRegionCode: country.value || 'PE',
                    delimiter: '-',
                  }}
                  className={classNames(inputStyle.input, { [inputStyle.red]: phone.errors && phone.errors.length },
                    { [inputStyle.lightBlue]: !!phone.value }, style.input)}
                  onChange={handleChange}
                  disabled={isPostingPhone}
                  name="phone"/>
          {phoneError}
        </section>
        {showAcceptSMS && handleAcceptReceiveSMS && !isPhoneVerified &&
        <section className={classNames(formStyle.row)}>
          <FormattedMessage id="preferences.security.phone.sms.label">{
            text => <Checkbox handleChange={handleAcceptReceiveSMS}
                              name="agreeToReceiveSMS"
                              value="agreeToReceiveSMS"
                              isChecked={agreeToReceiveSMS}
                              color={color === 'orange' ? 'orange' : 'lightBlue'}>
              {text}
            </Checkbox>
          }</FormattedMessage>
        </section>}
        {/*user has no phone or user has phone and has edited it or phone is verified*/}
        {(!phone.value || (!!phone.value && !phone.pristine) || isPhoneVerified) &&
        <section className={classNames(formStyle.row, formStyle.inputRow, style.button)}>
          <FormattedMessage id="preferences.security.saveChange">{
            txt => <button className={classNames(buttonStyle.btn,
              { [buttonStyle.lightBlue]: color === 'light-blue' },
              { [buttonStyle.orange]: color === 'orange' })}
                           disabled={isPostingPhone || (phone.pristine && isPhoneVerified)}
                           onClick={handleSubmitPhone}>{txt}</button>
          }</FormattedMessage>
        </section>}
        {/*user has phone and hasn't edited it and is not verified*/}
        {!!phone.value && phone.pristine && !!handleWillVerify && !isPhoneVerified &&
        <section className={classNames(formStyle.row, formStyle.inputRow, style.button)}>
          <FormattedMessage id="preferences.security.phone.verify.button">{
            txt => <button className={classNames(buttonStyle.btn,
              { [buttonStyle.lightBlue]: color === 'light-blue' },
              { [buttonStyle.orange]: color === 'orange' })}
                           onClick={handleWillVerify}>{txt}</button>
          }</FormattedMessage>
        </section>}
      </form>
    );
  }
}

PhoneForm.propTypes = {
  handleChangeCountry: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleFocus: PropTypes.func.isRequired,
  handleSubmitPhone: PropTypes.func.isRequired,
  handleWillVerify: PropTypes.func,
  handleAcceptReceiveSMS: PropTypes.func,
  isPostingPhone: PropTypes.bool,
  country: PropTypes.object.isRequired,
  phone: PropTypes.object.isRequired,
  color: PropTypes.string.isRequired,
  localValues: PropTypes.object.isRequired,
  agreeToReceiveSMS: PropTypes.bool,
  isPhoneVerified: PropTypes.bool,
};

PhoneForm.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/preferences/security/components/PhoneForm/PhoneForm.jsx